import { useState } from "react";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { submitEnquiry } from "@/lib/api";
import { categories } from "@/data/clusters";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  countryCode: "+1",
  website: "",
  nature_of_business: "",
  nature_of_business_other: "",
  country: "",
  category: "",
  moq: "",
  message: "",
};

const NATURE_OF_BUSINESS_OPTIONS = [
  "Retailer",
  "E-commerce",
  "Wholesaler",
  "Distributor",
  "Interior Designer",
  "Hospitality / Hotel",
  "Importer",
  "Brand Owner",
  "Buying Agency",
  "Other",
];

// Common ISO country-code dial-codes for the phone selector. Sorted by
// frequency-of-use for our buyer base.
const COUNTRY_DIAL_CODES = [
  { code: "+1", label: "🇺🇸 USA / Canada +1" },
  { code: "+44", label: "🇬🇧 United Kingdom +44" },
  { code: "+91", label: "🇮🇳 India +91" },
  { code: "+971", label: "🇦🇪 UAE +971" },
  { code: "+61", label: "🇦🇺 Australia +61" },
  { code: "+64", label: "🇳🇿 New Zealand +64" },
  { code: "+49", label: "🇩🇪 Germany +49" },
  { code: "+33", label: "🇫🇷 France +33" },
  { code: "+39", label: "🇮🇹 Italy +39" },
  { code: "+34", label: "🇪🇸 Spain +34" },
  { code: "+31", label: "🇳🇱 Netherlands +31" },
  { code: "+32", label: "🇧🇪 Belgium +32" },
  { code: "+41", label: "🇨🇭 Switzerland +41" },
  { code: "+43", label: "🇦🇹 Austria +43" },
  { code: "+45", label: "🇩🇰 Denmark +45" },
  { code: "+46", label: "🇸🇪 Sweden +46" },
  { code: "+47", label: "🇳🇴 Norway +47" },
  { code: "+353", label: "🇮🇪 Ireland +353" },
  { code: "+351", label: "🇵🇹 Portugal +351" },
  { code: "+27", label: "🇿🇦 South Africa +27" },
  { code: "+966", label: "🇸🇦 Saudi Arabia +966" },
  { code: "+974", label: "🇶🇦 Qatar +974" },
  { code: "+973", label: "🇧🇭 Bahrain +973" },
  { code: "+965", label: "🇰🇼 Kuwait +965" },
  { code: "+968", label: "🇴🇲 Oman +968" },
  { code: "+65", label: "🇸🇬 Singapore +65" },
  { code: "+852", label: "🇭🇰 Hong Kong +852" },
  { code: "+81", label: "🇯🇵 Japan +81" },
  { code: "+82", label: "🇰🇷 South Korea +82" },
  { code: "+55", label: "🇧🇷 Brazil +55" },
  { code: "+52", label: "🇲🇽 Mexico +52" },
  { code: "+other", label: "Other — enter with country code in number" },
];

// A small country list for the Country dropdown. Not exhaustive by design —
// we route by economic corridor and let 'Other' capture the tail.
const COUNTRY_OPTIONS = [
  "United States",
  "United Kingdom",
  "Canada",
  "Australia",
  "New Zealand",
  "Germany",
  "France",
  "Italy",
  "Spain",
  "Netherlands",
  "Belgium",
  "Switzerland",
  "Austria",
  "Denmark",
  "Sweden",
  "Norway",
  "Ireland",
  "Portugal",
  "South Africa",
  "United Arab Emirates",
  "Saudi Arabia",
  "Qatar",
  "Bahrain",
  "Kuwait",
  "Oman",
  "Singapore",
  "Hong Kong",
  "Japan",
  "South Korea",
  "Brazil",
  "Mexico",
  "India",
  "Other",
];

// Basic URL validation. Accepts optional scheme, requires a dot in the host.
const URL_RE = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)([\w\-._~:/?#[\]@!$&'()*+,;=%]*)?$/i;
// Basic international phone validation. Digits + optional spaces/dashes/parens.
const PHONE_RE = /^[\d\s\-()]{4,20}$/;


export const Contact = () => {
  useSeo({
    title: "Contact — Start an Enquiry · Moradabad",
    description:
      "Email Hello@asiasourcing.co.in or WhatsApp +91 90123 02222. Asia Sourcing, D-8 Manarover Colony, Delhi Road, Moradabad 244001, India. Reply within one working day.",
    path: "/contact",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        breadcrumb([
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact" },
        ]),
        {
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact Asia Sourcing",
          url: `${SITE_URL}/contact`,
        },
      ],
    },
    jsonLdId: "contact-jsonld",
  });

  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const [fieldErrors, setFieldErrors] = useState({});

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter your full name.";
    if (!form.company.trim()) errs.company = "Please enter your company name.";
    if (!form.email.trim()) errs.email = "Please enter your business email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      errs.email = "Please enter a valid business email.";
    if (!form.phone.trim()) errs.phone = "Please enter your phone number.";
    else if (!PHONE_RE.test(form.phone.trim()))
      errs.phone = "Please enter a valid phone number.";
    if (!form.website.trim()) errs.website = "Please enter your company website.";
    else if (!URL_RE.test(form.website.trim()))
      errs.website = "Please enter a valid website URL (e.g. https://www.company.com).";
    if (!form.nature_of_business) errs.nature_of_business = "Please select your nature of business.";
    if (form.nature_of_business === "Other" && !form.nature_of_business_other.trim())
      errs.nature_of_business_other = "Please describe your nature of business.";
    if (!form.country) errs.country = "Please select your country.";
    if (!form.category) errs.category = "Please select a product / category.";
    if (!form.message.trim()) errs.message = "Please describe your requirements.";
    return errs;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setStatus({
        state: "error",
        message: "Please complete the required fields marked below.",
      });
      // Scroll to first field with an error
      const firstErrKey = Object.keys(errs)[0];
      const el = document.getElementById(firstErrKey);
      if (el) el.focus({ preventScroll: false });
      return;
    }
    setStatus({ state: "loading", message: "" });
    try {
      const dial =
        form.countryCode && form.countryCode !== "+other" ? `${form.countryCode} ` : "";
      const payload = {
        name: form.name.trim(),
        company: form.company.trim(),
        email: form.email.trim(),
        phone: `${dial}${form.phone.trim()}`.trim(),
        website: form.website.trim(),
        nature_of_business: form.nature_of_business,
        nature_of_business_other:
          form.nature_of_business === "Other" ? form.nature_of_business_other.trim() : null,
        country: form.country,
        category: form.category,
        moq: form.moq.trim() || null,
        message: form.message.trim(),
      };
      const res = await submitEnquiry(payload);
      setStatus({
        state: "success",
        message: `Reference ${String(res.id).slice(0, 8).toUpperCase()}`,
      });
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = "Something didn’t go through. Please try again.";
      if (Array.isArray(detail)) {
        const first = detail[0];
        const field = first?.loc?.[first.loc.length - 1] ?? "field";
        msg = `Add a valid ${field} so we can route your enquiry.`;
      } else if (typeof detail === "string") {
        msg = detail;
      }
      setStatus({ state: "error", message: msg });
    }
  };

  return (
    <div data-testid="contact-page">
      <header className="page-head wrap">
        <Reveal as="p" className="eyebrow">Engage · Start an enquiry</Reveal>
        <WordReveal
          as="h1"
          text="Tell us the category. We'll find the cluster."
          stagger={65}
          testId="contact-headline"
        />
        <Reveal as="p" className="lede" delay={200}>
          A category, a rough quantity, and a sentence about what you need. We’ll come back within
          one working day with the right cluster and a costed next step.
        </Reveal>
      </header>

      <section className="wrap contact">
        <Reveal>
          {status.state === "success" ? (
            <div className="form-success" data-testid="form-success">
              <p className="mono">Enquiry received</p>
              <p>Thank you — we’ll come back to you within one working day. {status.message}</p>
              <button
                type="button"
                className="btn btn-ghost"
                style={{ marginTop: "1rem", paddingLeft: 0 }}
                onClick={() => setStatus({ state: "idle", message: "" })}
                data-testid="form-success-reset"
              >
                Send another enquiry
              </button>
            </div>
          ) : (
            <form className="contact-form" onSubmit={onSubmit} data-testid="contact-form" noValidate>
              <div className="field-row">
                <div className="field">
                  <label htmlFor="name">Full Name <span className="req">*</span></label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={onChange}
                    required
                    autoComplete="name"
                    aria-invalid={!!fieldErrors.name}
                    data-testid="form-name"
                  />
                  {fieldErrors.name && (
                    <p className="field-error" data-testid="err-name">{fieldErrors.name}</p>
                  )}
                </div>
                <div className="field">
                  <label htmlFor="company">Company Name <span className="req">*</span></label>
                  <input
                    id="company"
                    name="company"
                    type="text"
                    value={form.company}
                    onChange={onChange}
                    required
                    autoComplete="organization"
                    aria-invalid={!!fieldErrors.company}
                    data-testid="form-company"
                  />
                  {fieldErrors.company && (
                    <p className="field-error" data-testid="err-company">{fieldErrors.company}</p>
                  )}
                </div>
              </div>

              <div className="field-row">
                <div className="field">
                  <label htmlFor="email">Business Email <span className="req">*</span></label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    required
                    autoComplete="email"
                    aria-invalid={!!fieldErrors.email}
                    data-testid="form-email"
                  />
                  {fieldErrors.email && (
                    <p className="field-error" data-testid="err-email">{fieldErrors.email}</p>
                  )}
                </div>
                <div className="field">
                  <label htmlFor="website">Website <span className="req">*</span></label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    inputMode="url"
                    value={form.website}
                    onChange={onChange}
                    placeholder="https://www.company.com"
                    required
                    autoComplete="url"
                    aria-invalid={!!fieldErrors.website}
                    data-testid="form-website"
                  />
                  {fieldErrors.website && (
                    <p className="field-error" data-testid="err-website">{fieldErrors.website}</p>
                  )}
                </div>
              </div>

              <div className="field">
                <label htmlFor="phone">Phone Number <span className="req">*</span></label>
                <div className="phone-group">
                  <select
                    id="countryCode"
                    name="countryCode"
                    value={form.countryCode}
                    onChange={onChange}
                    className="phone-code"
                    aria-label="Country dial code"
                    data-testid="form-country-code"
                  >
                    {COUNTRY_DIAL_CODES.map((c) => (
                      <option key={c.code} value={c.code}>{c.label}</option>
                    ))}
                  </select>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="Phone number"
                    required
                    autoComplete="tel"
                    aria-invalid={!!fieldErrors.phone}
                    className="phone-input"
                    data-testid="form-phone"
                  />
                </div>
                {fieldErrors.phone && (
                  <p className="field-error" data-testid="err-phone">{fieldErrors.phone}</p>
                )}
              </div>

              <div className="field-row">
                <div className="field">
                  <label htmlFor="nature_of_business">Nature of Business <span className="req">*</span></label>
                  <select
                    id="nature_of_business"
                    name="nature_of_business"
                    value={form.nature_of_business}
                    onChange={onChange}
                    required
                    aria-invalid={!!fieldErrors.nature_of_business}
                    data-testid="form-nature"
                  >
                    <option value="" disabled>Select your business type</option>
                    {NATURE_OF_BUSINESS_OPTIONS.map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                  {fieldErrors.nature_of_business && (
                    <p className="field-error" data-testid="err-nature">{fieldErrors.nature_of_business}</p>
                  )}
                </div>
                <div className="field">
                  <label htmlFor="country">Country <span className="req">*</span></label>
                  <select
                    id="country"
                    name="country"
                    value={form.country}
                    onChange={onChange}
                    required
                    aria-invalid={!!fieldErrors.country}
                    data-testid="form-country"
                  >
                    <option value="" disabled>Select your country</option>
                    {COUNTRY_OPTIONS.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  {fieldErrors.country && (
                    <p className="field-error" data-testid="err-country">{fieldErrors.country}</p>
                  )}
                </div>
              </div>

              {form.nature_of_business === "Other" && (
                <div className="field">
                  <label htmlFor="nature_of_business_other">
                    Please describe your business <span className="req">*</span>
                  </label>
                  <input
                    id="nature_of_business_other"
                    name="nature_of_business_other"
                    type="text"
                    value={form.nature_of_business_other}
                    onChange={onChange}
                    aria-invalid={!!fieldErrors.nature_of_business_other}
                    data-testid="form-nature-other"
                  />
                  {fieldErrors.nature_of_business_other && (
                    <p className="field-error" data-testid="err-nature-other">
                      {fieldErrors.nature_of_business_other}
                    </p>
                  )}
                </div>
              )}

              <div className="field-row">
                <div className="field">
                  <label htmlFor="category">Product / Category Interested In <span className="req">*</span></label>
                  <select
                    id="category"
                    name="category"
                    value={form.category}
                    onChange={onChange}
                    required
                    aria-invalid={!!fieldErrors.category}
                    data-testid="form-category"
                  >
                    <option value="" disabled>Select a category</option>
                    {categories.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                    <option value="Mixed">Mixed / not sure</option>
                  </select>
                  {fieldErrors.category && (
                    <p className="field-error" data-testid="err-category">{fieldErrors.category}</p>
                  )}
                </div>
                <div className="field">
                  <label htmlFor="moq">Approx. MOQ <span className="opt">(optional)</span></label>
                  <input
                    id="moq"
                    name="moq"
                    type="text"
                    placeholder="e.g. 500 units"
                    value={form.moq}
                    onChange={onChange}
                    data-testid="form-moq"
                  />
                </div>
              </div>

              <div className="field">
                <label htmlFor="message">Message / Requirements <span className="req">*</span></label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={onChange}
                  required
                  aria-invalid={!!fieldErrors.message}
                  data-testid="form-message"
                  placeholder="A sentence on the product, target landed cost, and timeline."
                />
                {fieldErrors.message && (
                  <p className="field-error" data-testid="err-message">{fieldErrors.message}</p>
                )}
              </div>

              {status.state === "error" && (
                <p className="form-error" data-testid="form-error">{status.message}</p>
              )}

              <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginTop: ".4rem", flexWrap: "wrap" }}>
                <button
                  type="submit"
                  className="btn btn-fill btn-lg btn-glow"
                  disabled={status.state === "loading"}
                  data-testid="form-submit"
                >
                  <span>{status.state === "loading" ? "Sending…" : "Send enquiry"}</span>
                </button>
                <span className="mono">Reply within 1 working day</span>
              </div>
            </form>
          )}
        </Reveal>

        <Reveal>
          <aside className="contact-aside">
            <h3>What we need from you.</h3>
            <p>
              Plain guidance, not a brief in full. A category, a rough quantity, and a sentence on
              what you need. We’ll come back with the right cluster.
            </p>
            <div className="aside-item">
              <span>Response SLA</span>
              <strong>1 working day</strong>
            </div>
            <div className="aside-item">
              <span>Email</span>
              <strong>Hello@asiasourcing.co.in</strong>
            </div>
            <div className="aside-item">
              <span>Phone</span>
              <strong>+91 90123 02222</strong>
            </div>
            <div className="aside-item">
              <span>Base</span>
              <strong>D-8 Manarover Colony<br />Delhi Road, Moradabad 244001<br />Uttar Pradesh · India</strong>
            </div>
            <div className="aside-item">
              <span>Ports active</span>
              <strong>Nhava Sheva · Mundra · Chennai +3</strong>
            </div>
          </aside>
        </Reveal>
      </section>
    </div>
  );
};

export default Contact;
